import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BookOpenText,
  DownloadSimple,
  FileText,
  PencilSimpleLine,
  UploadSimple,
} from "@phosphor-icons/react";
import { httpsCallable } from "firebase/functions";
import { AppShell } from "@/components/layout/AppShell";
import { ChapterReader } from "@/components/review/ChapterReader";
import { EditorServicePanel } from "@/components/review/EditorServicePanel";
import { ReviewCountdown } from "@/components/review/ReviewCountdown";
import { Button, Spinner } from "@/components/ui";
import { functions } from "@/config/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useEditorService } from "@/hooks/useEditorService";
import { usePipelineStages } from "@/hooks/usePipelineStages";
import { useProject } from "@/hooks/useProject";
import { getChapters } from "@/lib/firestore/chapters";
import {
  countWords,
  parseManuscriptMarkdown,
  serializeManuscriptMarkdown,
  type EditableManuscriptChapter,
} from "@/lib/manuscript/markdown";
import { uploadFile } from "@/lib/storage/upload";
import {
  BookCover,
  ChecklistRow,
  PressEyebrow,
  PressPanel,
  StatusPill,
} from "@/components/pullpress/PressVisuals";
import type { Chapter, PipelineStage } from "@/types";

type WorkspaceMode = "write" | "review" | "proof";

interface DraftManuscriptResponse {
  markdown: string;
  source_path: string;
  source_type: "draft" | "revised";
  word_count: number;
}

interface RevisionImportResponse {
  revision_id: string;
  word_count: number;
  diff_summary: string;
}

interface HumanEditorRequestResponse {
  job_id: string;
  status: string;
  already_active: boolean;
}

interface SubmitEditorRevisionResponse {
  job_id: string;
  status: "author_review";
  word_count: number;
  delivered_late: boolean;
}

interface ApproveEditorRevisionResponse {
  job_id: string;
  revision_id: string;
  word_count: number;
  project_state: "REVISED";
}

interface PostEditorMessageResponse {
  message_id: string;
}

interface DraftSource {
  type: "draft" | "revised" | "chapter_records";
  path: string;
  wordCount: number;
}

const WORKSPACE_MODES: Array<{ value: WorkspaceMode; label: string; description: string }> = [
  { value: "write", label: "Edit", description: "Shape chapter text" },
  { value: "review", label: "Read", description: "Read the manuscript flow" },
  { value: "proof", label: "Proof", description: "Preview the proof packet" },
];

export function ReviewPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuthContext();
  const { project, loading: projectLoading } = useProject(projectId ?? null);
  const pipelineState = usePipelineStages(projectId ?? null);
  const editorService = useEditorService(projectId ?? null);
  const [chapters, setChapters] = useState<EditableManuscriptChapter[]>([]);
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const [activeMode, setActiveMode] = useState<WorkspaceMode>("write");
  const [draftSource, setDraftSource] = useState<DraftSource | null>(null);
  const [loadWarning, setLoadWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [requestingEditor, setRequestingEditor] = useState(false);
  const [postingEditorMessage, setPostingEditorMessage] = useState(false);
  const [submittingEditorRevision, setSubmittingEditorRevision] = useState(false);
  const [approvingEditorRevision, setApprovingEditorRevision] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function loadFallbackChapters(reason: string) {
      const firestoreChapters = await getChapters(projectId!);
      if (cancelled) return;
      setChapters(
        firestoreChapters.map((chapter: Chapter) => ({
          order: chapter.order,
          title: chapter.title,
          content: chapter.summary,
          word_count: chapter.word_count,
        })),
      );
      setDraftSource({
        type: "chapter_records",
        path: `projects/${projectId}/chapters`,
        wordCount: firestoreChapters.reduce((sum, chapter) => sum + chapter.word_count, 0),
      });
      setLoadWarning(reason);
    }

    async function loadManuscript() {
      setLoading(true);
      setLoadWarning(null);
      try {
        const getDraftManuscript = httpsCallable<
          { project_id: string; prefer_revised: boolean },
          DraftManuscriptResponse
        >(functions, "getDraftManuscript");
        const result = await getDraftManuscript({ project_id: projectId!, prefer_revised: true });
        const parsed = parseManuscriptMarkdown(result.data.markdown);
        if (parsed.chapters.length === 0) {
          await loadFallbackChapters("Loaded the chapter map because the manuscript file has no generated chapter headings.");
          return;
        }
        if (!cancelled) {
          setChapters([...parsed.chapters]);
          setDraftSource({
            type: result.data.source_type,
            path: result.data.source_path,
            wordCount: result.data.word_count,
          });
        }
      } catch (error) {
        const reason = error instanceof Error ? error.message : "Proof manuscript unavailable";
        await loadFallbackChapters(`Loaded the chapter map because the manuscript file could not be loaded: ${reason}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadManuscript();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (chapters.length === 0) {
      setActiveChapterIndex(0);
      return;
    }
    if (activeChapterIndex >= chapters.length) {
      setActiveChapterIndex(chapters.length - 1);
    }
  }, [activeChapterIndex, chapters.length]);

  const isReviewPhase = project?.state === "AWAITING_REVIEW";
  const isAuthor = Boolean(project && user?.uid === project.author_id);
  const isAssignedEditor = Boolean(
    project &&
    user &&
    editorService.job?.assigned_editor_id === user.uid &&
    ["assigned", "in_edit", "author_review", "revision_requested"].includes(editorService.job.status),
  );
  const canSubmitEditorRevision = Boolean(
    isAssignedEditor &&
    editorService.job &&
    ["assigned", "in_edit", "revision_requested"].includes(editorService.job.status) &&
    chapters.length > 0,
  );
  const canApproveEditorRevision = Boolean(
    isAuthor &&
    editorService.job?.status === "author_review" &&
    editorService.job.pending_revision_path,
  );
  const canRequestEditor = Boolean(
    isAuthor &&
    isReviewPhase &&
    (!editorService.job || ["approved", "completed", "cancelled"].includes(editorService.job.status)),
  );
  const qualityRows = useMemo(
    () => buildQualityRows(pipelineState.stages, isReviewPhase),
    [pipelineState.stages, isReviewPhase],
  );

  const updateChapter = useCallback((index: number, patch: Partial<Pick<EditableManuscriptChapter, "title" | "content">>) => {
    setChapters((current) =>
      current.map((chapter, currentIndex) => {
        if (currentIndex !== index) return chapter;
        const nextContent = patch.content ?? chapter.content;
        return {
          ...chapter,
          ...patch,
          word_count: countWords(nextContent),
        };
      }),
    );
    setDirty(true);
  }, []);

  const submitRevisionFile = useCallback(async (file: File, successPrefix: string) => {
    if (!projectId || !user) return;
    setUploading(true);
    try {
      const storagePath = await uploadFile(file, projectId, "text");
      const importFn = httpsCallable<
        { project_id: string; revised_storage_path: string },
        RevisionImportResponse
      >(functions, "importRevisedDraft");
      const result = await importFn({ project_id: projectId, revised_storage_path: storagePath });
      setDirty(false);
      setActiveMode("proof");
      setMessage(`${successPrefix} ${result.data.diff_summary}`);
    } catch (err) {
      setMessage(`Upload failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setUploading(false);
    }
  }, [projectId, user]);

  const handleInlineSubmit = useCallback(async () => {
    if (!project || chapters.length === 0) return;
    const markdown = serializeManuscriptMarkdown({
      title: project.title,
      authorName: project.author_name,
      chapters,
    });
    if (canSubmitEditorRevision) {
      setSubmittingEditorRevision(true);
      try {
        const submitFn = httpsCallable<
          { project_id: string; markdown: string },
          SubmitEditorRevisionResponse
        >(functions, "submitEditorRevision");
        const result = await submitFn({ project_id: project.id, markdown });
        setDirty(false);
        setMessage(`Editor revision is with the author for approval. ${result.data.word_count.toLocaleString()} words.`);
      } catch (err) {
        setMessage(`Editor revision failed: ${err instanceof Error ? err.message : "Unknown error"}`);
      } finally {
        setSubmittingEditorRevision(false);
      }
      return;
    }
    if (!isAuthor) {
      setMessage("Only the author or assigned editor can submit this manuscript.");
      return;
    }
    if (!isReviewPhase) {
      setMessage("The proof room is not open yet, so manuscript submission is locked.");
      return;
    }
    const file = new File([markdown], "pullpress-revision.md", { type: "text/markdown" });
    await submitRevisionFile(file, "In-app revision submitted.");
  }, [canSubmitEditorRevision, chapters, isAuthor, isReviewPhase, project, submitRevisionFile]);

  const handleRevisionUpload = useCallback(async (file: File) => {
    await submitRevisionFile(file, "Revision submitted.");
  }, [submitRevisionFile]);

  const handleExport = useCallback(async () => {
    if (!projectId) return;
    setExporting(true);
    try {
      const exportFn = httpsCallable<{ project_id: string }, { download_url: string }>(functions, "exportDraftDocx");
      const result = await exportFn({ project_id: projectId });
      window.open(result.data.download_url, "_blank");
      setMessage("Proof downloaded. Edit offline, then bring the revision back into the proof room.");
    } catch (err) {
      setMessage(`Export failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setExporting(false);
    }
  }, [projectId]);

  const handleRequestEditor = useCallback(async () => {
    if (!projectId) return;
    setRequestingEditor(true);
    try {
      const requestFn = httpsCallable<
        { project_id: string; scope: "line_edit" },
        HumanEditorRequestResponse
      >(functions, "requestHumanEditor");
      const result = await requestFn({ project_id: projectId, scope: "line_edit" });
      setMessage(result.data.already_active ? "Editor request already active." : "Editor request created. An operator can now assign the editor.");
    } catch (err) {
      setMessage(`Editor request failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setRequestingEditor(false);
    }
  }, [projectId]);

  const handlePostEditorMessage = useCallback(async (body: string) => {
    if (!projectId) return;
    setPostingEditorMessage(true);
    try {
      const postFn = httpsCallable<
        { project_id: string; body: string },
        PostEditorMessageResponse
      >(functions, "postEditorMessage");
      await postFn({ project_id: projectId, body });
    } catch (err) {
      setMessage(`Message failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setPostingEditorMessage(false);
    }
  }, [projectId]);

  const handleApproveEditorRevision = useCallback(async () => {
    if (!projectId) return;
    setApprovingEditorRevision(true);
    try {
      const approveFn = httpsCallable<
        { project_id: string },
        ApproveEditorRevisionResponse
      >(functions, "approveEditorRevision");
      const result = await approveFn({ project_id: projectId });
      setMessage(`Editor revision approved. Revision ${result.data.revision_id} is now the production manuscript.`);
    } catch (err) {
      setMessage(`Approval failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setApprovingEditorRevision(false);
    }
  }, [projectId]);

  if (projectLoading || loading) {
    return <AppShell variant="auth"><div className="flex justify-center py-20"><Spinner /></div></AppShell>;
  }

  if (!project) {
    return (
      <AppShell variant="auth">
        <div className="py-20 text-center"><PressEyebrow className="justify-center">Manuscript not found</PressEyebrow></div>
      </AppShell>
    );
  }

  const deadline = project.review_deadline?.toMillis?.() ?? (Date.now() + 14 * 24 * 60 * 60 * 1000);
  const totalWords = chapters.reduce((sum, chapter) => sum + chapter.word_count, 0);

  return (
    <AppShell variant="auth">
      <div className="sticky top-[72px] z-20 border-b border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/90 px-4 py-3 shadow-as-1 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-[1360px] flex-col justify-between gap-3 md:flex-row md:items-center">
          <div>
            <PressEyebrow>{isReviewPhase ? "Proof room" : `Status · ${productionStateLabel(project.state)}`}</PressEyebrow>
            <h1 className="mt-1 font-display text-2xl font-semibold text-ink-1">{project.title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <WorkspaceModeTabs activeMode={activeMode} onChange={setActiveMode} />
            {isAuthor && isReviewPhase && (
              <>
                <Button variant="secondary" size="sm" onClick={handleExport} disabled={exporting}>
                  <DownloadSimple size={16} className="mr-2" />
                  {exporting ? "Exporting..." : "Download proof"}
                </Button>
                <Button variant="walnut" size="sm" onClick={handleInlineSubmit} disabled={uploading || chapters.length === 0 || !dirty}>
                  <UploadSimple size={16} className="mr-2" />
                  {uploading ? "Submitting..." : dirty ? "Submit proof changes" : "No changes"}
                </Button>
              </>
            )}
            {canSubmitEditorRevision && (
              <Button variant="walnut" size="sm" onClick={handleInlineSubmit} disabled={submittingEditorRevision || chapters.length === 0}>
                <UploadSimple size={16} className="mr-2" />
                {submittingEditorRevision ? "Submitting..." : "Send to author"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-[1360px] px-4 pt-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-4 rounded-as-3 border border-walnut bg-walnut-tint p-3 font-ui text-t-14 text-ink-1">
            <span>{message}</span>
            <button onClick={() => setMessage(null)} className="font-mono text-[11px] uppercase tracking-as-eyebrow text-walnut-deep hover:text-ink-1">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {isReviewPhase && (
        <div className="mx-auto max-w-[1360px] px-4 pt-4 sm:px-6 lg:px-8">
          <ReviewCountdown deadlineTimestamp={deadline} />
        </div>
      )}

      <div className="mx-auto grid max-w-[1360px] gap-4 px-4 py-5 sm:px-6 xl:grid-cols-[minmax(0,1fr)_320px] lg:px-8">
        <main className="overflow-hidden rounded-as-3 border border-rule bg-paper-raised">
          {chapters.length > 0 ? (
            <WorkspaceMain
              mode={activeMode}
              chapters={chapters}
              activeChapterIndex={activeChapterIndex}
              setActiveChapterIndex={setActiveChapterIndex}
              updateChapter={updateChapter}
              bookTitle={project.title}
              authorName={project.author_name}
              onExport={handleExport}
              exporting={exporting}
            />
          ) : (
            <DraftPreparingPanel exporting={exporting} onExport={handleExport} reviewPhase={isReviewPhase} />
          )}
        </main>

        <aside className="space-y-4">
          <EditorServicePanel
            job={editorService.job}
            messages={editorService.messages}
            loading={editorService.loading}
            messagesLoading={editorService.messagesLoading}
            error={editorService.error}
            messagesError={editorService.messagesError}
            isAuthor={isAuthor}
            isAssignedEditor={isAssignedEditor}
            requesting={requestingEditor}
            posting={postingEditorMessage}
            submitting={submittingEditorRevision}
            approving={approvingEditorRevision}
            canRequest={canRequestEditor}
            canSubmitEditorRevision={canSubmitEditorRevision}
            canApproveEditorRevision={canApproveEditorRevision}
            onRequest={handleRequestEditor}
            onPostMessage={handlePostEditorMessage}
            onSubmitEditorRevision={handleInlineSubmit}
            onApproveEditorRevision={handleApproveEditorRevision}
          />

          <PressPanel className="p-4">
            <div className="grid grid-cols-[82px_1fr] gap-3">
              <BookCover title={project.title} subtitle="Proof edition" author={project.author_name} tone="mountain" className="w-20" />
              <div>
                <PressEyebrow>Proof packet</PressEyebrow>
                <h2 className="mt-2 font-display text-xl font-semibold leading-tight text-ink-1">{project.title}</h2>
                <StatusPill tone={isReviewPhase ? "editing" : "progress"}>{isReviewPhase ? "Proof window open" : productionStateLabel(project.state)}</StatusPill>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <ManuscriptMetric label="Chapters" value={String(chapters.length)} />
              <ManuscriptMetric label="Words" value={totalWords.toLocaleString()} />
            </div>
          </PressPanel>

          <PressPanel className="p-4">
            <PressEyebrow>Proof tools</PressEyebrow>
            <div className="mt-4 space-y-3">
              <ToolRow title="Edit" body="Shape chapter titles and manuscript text inside the app." active={activeMode === "write"} />
              <ToolRow title="Read" body="Read the book without editing controls." active={activeMode === "review"} />
              <ToolRow title="Proof" body="Preview a print-like packet before the release path." active={activeMode === "proof"} />
            </div>
          </PressPanel>

          <PressPanel className="p-4">
            <PressEyebrow>Manuscript source</PressEyebrow>
            <p className="mt-3 break-words font-ui text-sm leading-as-read text-ink-2">
              {draftSource ? sourceLabel(draftSource) : "No manuscript source loaded."}
            </p>
            {loadWarning && <p className="mt-3 font-ui text-sm leading-as-read text-flag">{loadWarning}</p>}
          </PressPanel>

          <PressPanel className="p-4">
            <PressEyebrow>Quality gates</PressEyebrow>
            {pipelineState.loading ? (
              <div className="mt-4"><Spinner /></div>
            ) : pipelineState.error ? (
              <p className="mt-4 font-ui text-sm leading-as-read text-flag">{pipelineState.error}</p>
            ) : (
              <div className="mt-4">
                {qualityRows.map((row) => (
                  <ChecklistRow key={row.label} label={row.label} status={row.status} />
                ))}
              </div>
            )}
          </PressPanel>

          <PressPanel className="p-4">
            <PressEyebrow>Final proof actions</PressEyebrow>
            <div className="mt-4 grid gap-3">
              {isAuthor ? (
                <>
                  <Button variant="secondary" onClick={handleExport} disabled={exporting}>
                    <FileText size={16} className="mr-2" />
                    Download proof DOCX
                  </Button>
                  <label>
                    <span className="inline-flex min-h-11 w-full items-center justify-center rounded-as-2 border border-rule-strong bg-transparent px-[18px] py-[10px] font-ui text-t-14 font-semibold text-ink-1 transition-colors hover:bg-paper-2">
                      <UploadSimple size={16} className="mr-2" />
                      Bring back offline revision
                    </span>
                    <input
                      type="file"
                      accept=".txt,.md,.docx"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void handleRevisionUpload(file);
                      }}
                      disabled={uploading}
                    />
                  </label>
                </>
              ) : (
                <Button variant="walnut" onClick={handleInlineSubmit} disabled={!canSubmitEditorRevision || submittingEditorRevision}>
                  <UploadSimple size={16} className="mr-2" />
                  {submittingEditorRevision ? "Submitting..." : "Send revision to author"}
                </Button>
              )}
            </div>
          </PressPanel>
        </aside>
      </div>
    </AppShell>
  );
}

function WorkspaceModeTabs({ activeMode, onChange }: {
  activeMode: WorkspaceMode;
  onChange: (mode: WorkspaceMode) => void;
}) {
  return (
    <div className="flex min-h-11 rounded-as-2 border border-rule bg-paper p-1">
      {WORKSPACE_MODES.map((mode) => (
        <button
          key={mode.value}
          type="button"
          onClick={() => onChange(mode.value)}
          className={`min-h-9 rounded-as-1 px-3 font-ui text-sm font-semibold transition-colors ${activeMode === mode.value ? "bg-walnut text-paper-raised shadow-as-1" : "text-ink-3 hover:text-ink-1"}`}
          title={mode.description}
        >
          {mode.label}
        </button>
      ))}
    </div>
  );
}

function WorkspaceMain({
  mode,
  chapters,
  activeChapterIndex,
  setActiveChapterIndex,
  updateChapter,
  bookTitle,
  authorName,
  onExport,
  exporting,
}: {
  mode: WorkspaceMode;
  chapters: readonly EditableManuscriptChapter[];
  activeChapterIndex: number;
  setActiveChapterIndex: (index: number) => void;
  updateChapter: (index: number, patch: Partial<Pick<EditableManuscriptChapter, "title" | "content">>) => void;
  bookTitle: string;
  authorName: string;
  onExport: () => void;
  exporting: boolean;
}) {
  if (mode === "review") {
    return <ChapterReader chapters={chapters} bookTitle={bookTitle} authorName={authorName} />;
  }

  if (mode === "proof") {
    return <ProofPreview chapters={chapters} bookTitle={bookTitle} authorName={authorName} onExport={onExport} exporting={exporting} />;
  }

  return (
    <ChapterEditor
      chapters={chapters}
      activeChapterIndex={activeChapterIndex}
      setActiveChapterIndex={setActiveChapterIndex}
      updateChapter={updateChapter}
      bookTitle={bookTitle}
      authorName={authorName}
    />
  );
}

function ChapterEditor({
  chapters,
  activeChapterIndex,
  setActiveChapterIndex,
  updateChapter,
  bookTitle,
  authorName,
}: {
  chapters: readonly EditableManuscriptChapter[];
  activeChapterIndex: number;
  setActiveChapterIndex: (index: number) => void;
  updateChapter: (index: number, patch: Partial<Pick<EditableManuscriptChapter, "title" | "content">>) => void;
  bookTitle: string;
  authorName: string;
}) {
  const chapter = chapters[activeChapterIndex] ?? chapters[0];

  return (
    <div className="grid min-h-[calc(100dvh-150px)] bg-paper lg:h-[calc(100dvh-150px)] lg:grid-cols-[240px_minmax(0,1fr)_280px]">
      <aside className="border-b border-rule bg-paper-raised/80 p-3 backdrop-blur-2xl lg:overflow-y-auto lg:border-b-0 lg:border-r">
        <PressEyebrow>Chapters</PressEyebrow>
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 lg:block lg:space-y-1 lg:overflow-visible lg:pb-0">
          {chapters.map((candidate, index) => (
            <button
              key={candidate.order}
              type="button"
              onClick={() => setActiveChapterIndex(index)}
              className={`min-w-[170px] rounded-as-2 px-3 py-2 text-left transition-colors lg:min-w-0 lg:w-full ${index === activeChapterIndex ? "border-l-[3px] border-walnut bg-paper-raised shadow-as-1" : "hover:bg-paper-raised/70"}`}
            >
              <span className="block truncate font-display text-base font-semibold text-ink-1">Ch. {candidate.order} · {candidate.title}</span>
              <span className="mt-1 block font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">{candidate.word_count.toLocaleString()} words</span>
            </button>
          ))}
        </div>
      </aside>

      <section className="min-w-0 overflow-y-auto bg-[linear-gradient(180deg,var(--as-paper),var(--as-paper-2))] px-4 py-5 sm:px-6">
        <div className="mx-auto max-w-[var(--as-editor-measure)] rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised p-4 shadow-as-2 sm:p-6">
          <PressEyebrow>Edit · {bookTitle} · {authorName}</PressEyebrow>
          <label className="mt-4 block">
            <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">Chapter title</span>
            <input
              value={chapter.title}
              onChange={(event) => updateChapter(activeChapterIndex, { title: event.target.value })}
              className="mt-2 h-12 w-full rounded-as-2 border border-rule bg-paper px-3 font-display text-2xl font-semibold text-ink-1 outline-none focus:border-walnut"
            />
          </label>
          <label className="mt-4 block">
            <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">Manuscript text</span>
            <textarea
              value={chapter.content}
              onChange={(event) => updateChapter(activeChapterIndex, { content: event.target.value })}
              className="mt-2 min-h-[52vh] w-full resize-y rounded-as-3 border border-rule bg-paper px-4 py-4 font-text text-[18px] leading-as-read text-ink-2 outline-none placeholder:text-ink-4 focus:border-walnut"
              spellCheck
            />
          </label>
          <div className="mt-4 flex flex-col justify-between gap-3 border-t border-rule pt-4 sm:flex-row sm:items-center">
            <span className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">
              {chapter.word_count.toLocaleString()} words
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setActiveChapterIndex(Math.max(0, activeChapterIndex - 1))}
                disabled={activeChapterIndex === 0}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                onClick={() => setActiveChapterIndex(Math.min(chapters.length - 1, activeChapterIndex + 1))}
                disabled={activeChapterIndex === chapters.length - 1}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </section>

      <aside className="border-t border-rule bg-paper-raised/80 p-4 backdrop-blur-2xl lg:border-l lg:border-t-0">
        <PressEyebrow>Proof state</PressEyebrow>
        <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-1">
          <ManuscriptMetric label="Active" value={`Ch. ${chapter.order}`} />
          <ManuscriptMetric label="Words" value={chapter.word_count.toLocaleString()} />
          <ManuscriptMetric label="Chapters" value={String(chapters.length)} />
          <ManuscriptMetric label="Mode" value="Edit" />
        </div>
        <p className="mt-4 font-ui text-sm leading-as-read text-ink-3">
          Changes stay in this session until you submit them. Submission creates a revision for the production manuscript.
        </p>
      </aside>
    </div>
  );
}

function ProofPreview({
  chapters,
  bookTitle,
  authorName,
  onExport,
  exporting,
}: {
  chapters: readonly EditableManuscriptChapter[];
  bookTitle: string;
  authorName: string;
  onExport: () => void;
  exporting: boolean;
}) {
  return (
    <div className="max-h-[calc(100dvh-150px)] overflow-y-auto bg-[linear-gradient(180deg,var(--as-paper),var(--as-paper-2))] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-[var(--as-reader-measure)]">
        <div className="mb-4 flex flex-col justify-between gap-3 rounded-as-3 border border-rule bg-paper-raised p-4 shadow-as-1 sm:flex-row sm:items-center">
          <div>
            <PressEyebrow>Proof preview</PressEyebrow>
            <h2 className="mt-2 font-display text-2xl font-semibold text-ink-1">{bookTitle}</h2>
            <p className="font-ui text-sm text-ink-3">{authorName}</p>
          </div>
          <Button variant="secondary" onClick={onExport} disabled={exporting}>
            <DownloadSimple size={16} className="mr-2" />
            {exporting ? "Exporting..." : "Download DOCX"}
          </Button>
        </div>

        <article className="rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised px-5 py-10 shadow-as-2 sm:px-10">
          <header className="mb-12 text-center">
            <PressEyebrow className="justify-center">PullPress proof</PressEyebrow>
            <h1 className="mt-4 font-display text-t-49 font-semibold leading-as-tight text-ink-1">{bookTitle}</h1>
            <p className="mt-3 font-text text-lg text-ink-2">{authorName}</p>
          </header>
          {chapters.map((chapter) => (
            <section key={chapter.order} className="border-t border-rule py-8 first:border-t-0 first:pt-0">
              <PressEyebrow>Chapter {chapter.order} · {chapter.word_count.toLocaleString()} words</PressEyebrow>
              <h2 className="mt-2 font-display text-t-37 font-semibold leading-as-tight text-ink-1">{chapter.title}</h2>
              <div className="mt-5 font-text text-[18px] leading-as-read text-ink-2">
                {chapter.content.split(/\n\n+/u).map((paragraph, index) => (
                  <p key={`${chapter.order}-${index}`} className="mb-5">{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}

function DraftPreparingPanel({ exporting, onExport, reviewPhase }: {
  exporting: boolean;
  onExport: () => void;
  reviewPhase: boolean;
}) {
  return (
    <div className="mx-auto max-w-2xl px-5 py-12 text-center sm:px-8">
      <BookOpenText size={40} weight="duotone" className="mx-auto text-walnut" />
      <PressEyebrow dot className="mt-4 justify-center">Proof packet</PressEyebrow>
      <h2 className="mt-3 font-display text-3xl font-semibold text-ink-1">The proof room is being prepared.</h2>
      <p className="mt-3 font-text text-base leading-as-read text-ink-2">
        The proof room opens when the manuscript or chapter map is available. You can still export the proof packet or bring back a revision when the proof window is active.
      </p>
      {reviewPhase && (
        <Button variant="primary" onClick={onExport} disabled={exporting} className="mt-6">
          {exporting ? "Preparing..." : "Download the proof"}
        </Button>
      )}
    </div>
  );
}

function ManuscriptMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-as-2 border border-rule bg-paper p-3">
      <div className="font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold leading-none text-ink-1">{value}</div>
    </div>
  );
}

function ToolRow({ title, body, active = false }: { title: string; body: string; active?: boolean }) {
  return (
    <div className={`rounded-as-2 border p-3 ${active ? "border-walnut bg-walnut-tint" : "border-rule bg-paper"}`}>
      <p className="flex items-center gap-2 font-display text-lg font-semibold text-ink-1">
        {active && <PencilSimpleLine size={16} className="text-walnut" />}
        {title}
      </p>
      <p className="mt-1 font-text text-sm leading-as-read text-ink-3">{body}</p>
    </div>
  );
}

function buildQualityRows(stages: PipelineStage[], isReviewPhase: boolean): { label: string; status: "complete" | "warning" | "pending" }[] {
  const rows = stages.flatMap((stage) =>
    stage.verification_results.map((result) => ({
      label: `${stage.stage.replace(/_/g, " ")} · ${result.description || result.check_id}`,
      status: result.passed ? "complete" as const : result.blocking ? "warning" as const : "pending" as const,
    })),
  );

  if (rows.length > 0) return rows;

  return [
    { label: "Production quality results", status: "pending" },
    { label: "Author proof window", status: isReviewPhase ? "pending" : "complete" },
  ];
}

function sourceLabel(source: DraftSource): string {
  if (source.type === "chapter_records") {
    return `Chapter map fallback · ${source.wordCount.toLocaleString()} words · ${source.path}`;
  }
  return `${source.type === "revised" ? "Approved revision" : "Proof manuscript"} · ${source.wordCount.toLocaleString()} words · ${source.path}`;
}

function productionStateLabel(state: string): string {
  if (state === "PUBLISHED") return "Published";
  if (state === "PUBLISHING") return "Releasing";
  if (state === "PACKAGED") return "Packet ready";
  if (state === "EDITED") return "Edit complete";
  if (state === "AWAITING_REVIEW") return "Proof window open";
  if (state === "DRAFTED") return "Proof ready";
  if (state === "ERROR") return "Blocked";
  return "In production";
}
